import {
  DemoCountrySource,
  DemoInstitutionType,
  DemoRequestKind,
  DemoStudentCount,
  type RequestDemoInput,
} from '@/types/composition-functions';

// @allow-handwritten editable form state, not the API shape: blank strings while typing, a local
// phone without dial code and the honeypot; buildDemoRequestInput maps it to the generated RequestDemoInput.
export interface DemoRequestForm {
  name: string;
  email: string;
  phone: string;
  institution_name: string;
  institution_type: DemoInstitutionType | null;
  role: string;
  student_count: DemoStudentCount | null;
  country_code: string | null;
  message: string;
  /** Honeypot, rendered off-screen. */
  website: string;
}

export const emptyDemoRequestForm = (countryCode: string | null): DemoRequestForm => ({
  name: '',
  email: '',
  phone: '',
  institution_name: '',
  institution_type: null,
  role: '',
  student_count: null,
  country_code: countryCode,
  message: '',
  website: '',
});

export const INSTITUTION_TYPE_OPTIONS = (schoolLevels: string) => [
  { label: `${schoolLevels} school`, value: DemoInstitutionType.K12 },
  { label: 'Tertiary (college, polytechnic, university)', value: DemoInstitutionType.Tertiary },
  { label: 'Other (training institute, language centre…)', value: DemoInstitutionType.Other },
];

export const STUDENT_COUNT_OPTIONS = [
  { label: 'Up to 200', value: DemoStudentCount.UpTo_200 },
  { label: '201 – 500', value: DemoStudentCount.From_201To_500 },
  { label: '501 – 1,000', value: DemoStudentCount.From_501To_1000 },
  { label: '1,001 – 5,000', value: DemoStudentCount.From_1001To_5000 },
  { label: 'More than 5,000', value: DemoStudentCount.Over_5000 },
];

export const DEMO_DIALOG_COPY: Record<DemoRequestKind, { title: string; intro: string; submit: string }> = {
  [DemoRequestKind.Demo]: {
    title: 'Request a demo',
    intro: 'Tell us about your institution and we\'ll set up a 30-minute walkthrough for your use case.',
    submit: 'Request demo',
  },
  [DemoRequestKind.Sales]: {
    title: 'Talk to sales',
    intro: 'Tell us about your institution and we\'ll put a pricing proposal together.',
    submit: 'Send enquiry',
  },
};

/** Local number + the country's dial code, unless the visitor typed one. */
export function withDialCode(phone: string, dialCode: string): string {
  const trimmed = phone.trim();
  if (!trimmed || trimmed.startsWith('+') || !dialCode) return trimmed;
  return `${dialCode} ${trimmed.replace(/^0+/, '')}`;
}

const blankToNull = (value: string) => (value.trim() === '' ? null : value.trim());

export function buildDemoRequestInput(
  form: DemoRequestForm,
  kind: DemoRequestKind,
  dialCode: string,
  countrySource: DemoCountrySource | null,
): RequestDemoInput {
  return {
    kind,
    name: form.name.trim(),
    email: form.email.trim(),
    phone: blankToNull(withDialCode(form.phone, dialCode)),
    institution_name: form.institution_name.trim(),
    institution_type: form.institution_type,
    role: blankToNull(form.role),
    student_count: form.student_count,
    country_code: form.country_code,
    country_source: form.country_code ? (countrySource ?? DemoCountrySource.Manual) : null,
    message: blankToNull(form.message),
    website: blankToNull(form.website),
  };
}
